import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import { setFleetNow } from "../slices/fleetSlice";
import { useMemo, useState } from "react";
import "../App.css";

const DAY_MS = 24 * 60 * 60 * 1000;

function fmtTime(ts: number) {
  if (!ts) return "—";
  return new Date(ts).toLocaleTimeString();
}

function fmtDateTime(ts: number) {
  if (!ts) return "—";
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function dayFromStart(ts: number, start: number) {
  if (!ts || !start) return 1;
  return Math.floor((ts - start) / DAY_MS) + 1;
}

export default function TimeBar() {
  const dispatch = useDispatch();
  const fleet = useSelector((s: RootState) => s.fleet);

  const [showTip, setShowTip] = useState(false);

  const pct = useMemo(() => {
    const span = Math.max(1, fleet.end - fleet.start);
    return ((fleet.now - fleet.start) / span) * 100;
  }, [fleet.now, fleet.start, fleet.end]);

  const eventsCount = fleet.events.length;
  const tripsCount = Object.keys(fleet.trips).length;
  const eventPointer = fleet.pointer;

  const dayNow = dayFromStart(fleet.now, fleet.start);
  const rangeStart = fmtDateTime(fleet.start);
  const rangeEnd = fmtDateTime(fleet.end);

  return (
    <div className="grid gap-[10px] mt-2 mb-3">
      {/* Slider + tooltip */}
      <div className="grid gap-[6px]">
        <h3>Timeline Slider</h3>

        <div className="relative">
          <input
            type="range"
            className="timeline-slider appearance-none w-full h-[6px] rounded-[3px] bg-[#eff4ff] outline-none"
            min={fleet.start}
            max={fleet.end}
            step={1000}
            value={fleet.now}
            onChange={(e) => dispatch(setFleetNow(Number(e.target.value)))}
            onMouseEnter={() => setShowTip(true)}
            onMouseLeave={() => setShowTip(false)}
            onMouseDown={() => setShowTip(true)}
            onMouseUp={() => setShowTip(false)}
            onTouchStart={() => setShowTip(true)}
            onTouchEnd={() => setShowTip(false)}
            aria-label="Timeline"
            aria-valuemin={fleet.start}
            aria-valuemax={fleet.end}
            aria-valuenow={fleet.now}
            aria-valuetext={`${fmtTime(fleet.now)} · Day ${dayNow}`}
          />

          {/* floating tooltip that tracks the thumb */}
          <div
            className={`absolute bottom-[calc(80%+8px)] -translate-x-1/2 bg-[#111827] text-white text-[12px] leading-none px-2 py-[6px] rounded-[6px] whitespace-nowrap pointer-events-none  transition-transform duration-150 ease-linear z-[5] max-[480px]:max-w-[70vw] max-[480px]:overflow-hidden max-[480px]:text-ellipsis ${
              showTip
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-[2px]"
            }`}
            style={{ left: `${pct}%` }}
            role="presentation"
          >
            {fmtTime(fleet.now)} · Day {dayNow}
            <span className="absolute top-full left-1/2 w-0 h-0 -translate-x-1/2 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-[#111827]" />
          </div>
        </div>

        <div className="grid [grid-template-columns:1fr_auto_1fr] items-start text-[12px] text-gray-500 gap-[6px]">
          <span className="inline-flex flex-col gap-[2px]">
            <strong>{rangeStart.split(", ")[0]}</strong>
            <em>{rangeStart.split(", ").slice(1).join(", ")}</em>
          </span>

          <span className="justify-self-center text-[#0b1220] font-semibold">
            Day {dayNow}
          </span>

          <span className="inline-flex flex-col gap-[2px] justify-self-end">
            <strong>{rangeEnd.split(", ")[0]}</strong>
            <em>{rangeEnd.split(", ").slice(1).join(", ")}</em>
          </span>
        </div>
      </div>

      {/* Info block */}
      <div className="flex items-center gap-4 flex-wrap bg-[#eff4ff] py-2 px-3 rounded">
        <div className="text-[14px] text-[#0b1220]">
          <span className="font-semibold mr-1">Time:</span>
          <span className="mr-1">Day {dayNow}</span>
          {fmtDateTime(fleet.now)}
        </div>

        <div className="text-[14px] text-[#0b1220]">
          <span className="font-semibold mr-1">Range:</span>
          {rangeStart} – {rangeEnd}
        </div>

        <div className="text-[14px] text-[#0b1220]">
          <span className="font-semibold mr-1">Trips:</span>
          {tripsCount}
        </div>
        <div className="flex items-center gap-4 text-[14px] text-[#0b1220]">
          <span>
            <span className="font-semibold mr-1">Events:</span>
            {eventsCount}
          </span>
          <span>
            <span className="font-semibold mr-1">Event Pointer:</span>
            {eventPointer}
          </span>
        </div>
      </div>
    </div>
  );
}
